import { useState } from "react";
import { format, parse } from "date-fns";
import { Link } from "react-router-dom";
import { MapPin, Clock, Star } from "lucide-react";
import { computeDerived, sumShiftHours, sumComp, dayTourHours, gratuityColor, gratuityPctColor, toViewingsArray, formatValue } from "@/lib/fields";
import { useFieldConfig } from "@/contexts/FieldConfigContext";
import { ImageZoomViewer } from "@/components/ImageZoomViewer";

const fmtDate = (mdY) => {
  try {
    const d = parse(String(mdY), "MM/dd/yyyy", new Date());
    return isNaN(d.getTime()) ? mdY : format(d, "EEE, MMM d, yyyy");
  } catch {
    return mdY;
  }
};

const Row = ({ label, value, testid }) =>
  (value === undefined || value === null || value === "" || (Array.isArray(value) && !value.length)) ? null : (
    <div className="flex items-baseline justify-between gap-4 py-1.5" data-testid={testid}>
      <span className="shrink-0 text-xs uppercase tracking-wide text-zinc-500">{label}</span>
      <span className="min-w-0 text-right text-sm font-500 text-zinc-100">{value}</span>
    </div>
  );

const Card = ({ title, children }) => (
  <div className="rounded-2xl lg-glass p-5">
    <h3 className="mb-3 text-[11px] font-700 uppercase tracking-wider text-[#E0575B]">{title}</h3>
    <div className="divide-y divide-zinc-700/40">{children}</div>
  </div>
);

export const CompletedTourView = ({ guest, financialPrivacy }) => {
  const { customFields, hidden } = useFieldConfig();
  const [viewer, setViewer] = useState({ open: false, src: null, alt: "" });
  const isHidden = (k) => hidden.includes(k);
  const hasVal = (v) => !(v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length));
  const customShown = customFields.filter((f) => hasVal(guest[f.key]));
  const d = computeDerived(guest);
  const dayShifts = Array.isArray(guest.day_shifts) ? guest.day_shifts : [];
  // Legacy tours store total_hours/comp directly (no per-day shifts) — fall back to those.
  // For completed tours with no hours recorded at all, default the display to 7 hours.
  const totalHours = dayShifts.length ? sumShiftHours(dayShifts) : (Number(guest.total_hours) || 7);
  const totalComp = dayShifts.length ? sumComp(dayShifts) : (Number(guest.comp_time) || 0);
  const linked = Array.isArray(guest.linked_guests) ? guest.linked_guests.filter(Boolean) : [];
  const viewings = toViewingsArray(guest.viewings);
  const guestList = (Array.isArray(guest.guest_list) ? guest.guest_list : []).filter(
    (g) => g && (String(g.name || "").trim() || g.age !== null && g.age !== undefined && g.age !== "")
  );
  const money = !financialPrivacy;
  const tip = guest.tip_percent ?? d.tip_percent;
  const guestPhotos = (Array.isArray(guest.guest_photos) && guest.guest_photos.length)
    ? guest.guest_photos
    : (guest.party_photo ? [guest.party_photo] : []);

  return (
    <div className="space-y-5" data-testid="completed-tour-view">
      {/* Guests */}
      <Card title="Guests">
        <Row
          label="Lead Guest"
          value={guest.guest_name ? (
            <Link
              to={`/profile/${encodeURIComponent(guest.guest_name)}`}
              className="text-[#E0575B] underline-offset-2 hover:underline"
              data-testid="ro-lead-guest-link"
              aria-label={`View profile for ${guest.guest_name}`}
            >
              {guest.guest_name}
            </Link>
          ) : ""}
          testid="ro-lead-guest"
        />
        {linked.length > 0 && (
          <Row
            label="Also in party"
            value={
              <span className="inline-flex flex-wrap justify-end gap-x-1 gap-y-0.5">
                {linked.map((n, i) => (
                  <span key={n}>
                    <Link
                      to={`/profile/${encodeURIComponent(n)}`}
                      className="text-[#E0575B] underline-offset-2 hover:underline"
                      data-testid={`ro-linked-link-${i}`}
                      aria-label={`View profile for ${n}`}
                    >
                      {n}
                    </Link>
                    {i < linked.length - 1 ? "," : ""}
                  </span>
                ))}
              </span>
            }
            testid="ro-linked"
          />
        )}
        {guest.party_size ? <Row label="Party size" value={guest.party_size} testid="ro-party" /> : null}
      </Card>

      {/* Schedule */}
      <Card title="Schedule">
        {dayShifts.length === 0 && <Row label="Dates" value={(Array.isArray(guest.tour_date) ? guest.tour_date : [guest.tour_date].filter(Boolean)).join(", ")} />}
        {dayShifts.map((s, i) => (
          <div key={s.date + i} className="py-2" data-testid={`ro-day-${i}`}>
            <div className="mb-1 text-sm font-600 text-zinc-100">{fmtDate(s.date)}</div>
            <div className="space-y-0.5 text-sm text-zinc-300">
              {s.meet_location && (
                <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-[#E0575B]" /> {s.meet_location}</div>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-zinc-400">
                {(s.start || s.end) && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-zinc-400" /> {s.start} – {s.end}</span>}
                {s.meet_time && <span>Pickup {s.meet_time}</span>}
                {dayTourHours(s) ? <span>{dayTourHours(s)} hr tour</span> : null}
                {Number(s.comp) ? <span>{s.comp} comp</span> : null}
              </div>
            </div>
          </div>
        ))}
        {totalHours ? <Row label="Total tour hours" value={`${totalHours} hrs`} testid="ro-total-hours" /> : null}
        {totalComp ? <Row label="Total comp" value={`${totalComp} hrs`} testid="ro-total-comp" /> : null}
      </Card>

      {/* Tour details */}
      <Card title="Tour Details">
        {!isHidden("tour_type") && <Row label="Tour type" value={guest.tour_type} testid="ro-tour-type" />}
        {!isHidden("cost_type") && <Row label="Cost type" value={guest.cost_type} testid="ro-cost-type" />}
        {money && !isHidden("cost_per_hour") && <Row label="Avg hourly rate" value={guest.cost_per_hour ? `$${Number(guest.cost_per_hour).toLocaleString()}` : ""} testid="ro-rate" />}
        {money && !isHidden("retail_value") && <Row label="Total retail" value={d.retail_value ? `$${Number(d.retail_value).toLocaleString()}` : ""} testid="ro-retail" />}
        {!isHidden("promo") && <Row label="Promo" value={guest.promo} testid="ro-promo" />}
        {!isHidden("co_guide") && <Row label="Co-guide" value={guest.co_guide} testid="ro-coguide" />}
      </Card>

      {/* Post-tour */}
      <Card title="Post-Tour">
        {guest.notes ? (
          <div className="py-1.5" data-testid="ro-notes">
            <div className="mb-1 text-xs uppercase tracking-wide text-zinc-500">Notes</div>
            <p className="whitespace-pre-wrap text-sm text-zinc-200">{guest.notes}</p>
          </div>
        ) : null}
        {Number(guest.rating) > 0 && (
          <div className="flex items-center justify-between py-1.5" data-testid="ro-rating">
            <span className="text-xs uppercase tracking-wide text-zinc-500">Rating</span>
            <span className="inline-flex gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`h-4 w-4 ${n <= Number(guest.rating) ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`} />
              ))}
            </span>
          </div>
        )}
        {money && (guest.gratuity !== undefined && guest.gratuity !== null && guest.gratuity !== "") && (
          <div className="flex items-center justify-between py-1.5" data-testid="ro-gratuity">
            <span className="text-xs uppercase tracking-wide text-zinc-500">Gratuity</span>
            <span className="text-sm font-700">
              <span className={gratuityColor(tip)}>${Number(guest.gratuity).toLocaleString()}</span>
              {tip !== null && tip !== undefined && tip !== "" && <span className={`ml-1 ${gratuityPctColor(tip)}`}>({tip}%)</span>}
            </span>
          </div>
        )}
        {money && guest.gratuity_claimed && guest.gratuity_claimed_amount !== undefined && guest.gratuity_claimed_amount !== null && guest.gratuity_claimed_amount !== "" && (
          <div className="flex items-center justify-between py-1.5" data-testid="ro-gratuity-claimed">
            <span className="text-xs uppercase tracking-wide text-zinc-500">Claimed</span>
            <span className="text-sm font-700 text-zinc-100">${Number(guest.gratuity_claimed_amount).toLocaleString()}</span>
          </div>
        )}
        <Row label="Gratuity type" value={isHidden("tip_type") ? "" : guest.tip_type} testid="ro-tip-type" />
        {money && !isHidden("avg_per_guest") && <Row label="Avg / day" value={d.avg_per_guest ? `$${Number(d.avg_per_guest).toLocaleString()}` : ""} testid="ro-avg" />}
        {!isHidden("guest_food") && <Row label="Guest Food" value={guest.guest_food} testid="ro-food" />}
        {!isHidden("host_again") && <Row label="Would host again" value={guest.host_again === false ? "No" : "Yes"} testid="ro-host" />}
        {!isHidden("viewings") && viewings.length > 0 && <Row label="Viewings" value={viewings.join(", ")} testid="ro-viewings" />}
      </Card>

      {/* Custom fields */}
      {customShown.length > 0 && (
        <div className="rounded-2xl lg-glass p-5" data-testid="detail-custom-fields">
          <h3 className="mb-3 text-[11px] font-700 uppercase tracking-wider text-[#E0575B]">Custom Fields</h3>
          <div className="divide-y divide-zinc-700/40">
            {customShown.map((f) => (
              <Row key={f.key} label={f.label} value={formatValue(f, guest[f.key])} testid={`custom-field-${f.key}`} />
            ))}
          </div>
        </div>
      )}

      {/* Guest list (simplified) */}
      {guestList.length > 0 && (
        <div className="rounded-2xl lg-glass p-5" data-testid="ro-guest-list">
          <h3 className="mb-3 text-[11px] font-700 uppercase tracking-wider text-[#E0575B]">Guest List</h3>
          <div className="flex flex-wrap gap-2">
            {guestList.map((g, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100" data-testid={`ro-guest-list-item-${i}`}>
                {g.name || "Unnamed"}
                {g.age !== null && g.age !== undefined && g.age !== "" && (
                  <span className="rounded-full bg-zinc-800 px-1.5 text-xs text-zinc-300">age {g.age}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Photos — small tappable thumbnails; full-screen pinch-zoom & pan */}
      {(guestPhotos.length > 0 || guest.tour_sheet_image) && (
        <div className="rounded-2xl lg-glass p-5" data-testid="ro-photos">
          <h3 className="mb-3 text-[11px] font-700 uppercase tracking-wider text-[#E0575B]">Photos</h3>
          <div className="flex flex-wrap gap-6">
            {guestPhotos.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs uppercase tracking-wide text-zinc-400">Guest Photos</div>
                <div className="flex flex-wrap gap-2">
                  {guestPhotos.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Guest ${i + 1}`}
                      draggable={false}
                      onClick={() => setViewer({ open: true, src, alt: "Guest Photo" })}
                      className="h-28 w-28 cursor-zoom-in select-none rounded-lg border border-zinc-800 object-cover transition-opacity hover:opacity-90"
                      data-testid={`ro-guest-photo-${i}`}
                    />
                  ))}
                </div>
              </div>
            )}
            {guest.tour_sheet_image && (
              <div>
                <div className="mb-1.5 text-xs uppercase tracking-wide text-zinc-400">Tour Sheet</div>
                <img
                  src={guest.tour_sheet_image}
                  alt="Tour Sheet"
                  draggable={false}
                  onClick={() => setViewer({ open: true, src: guest.tour_sheet_image, alt: "Tour Sheet" })}
                  className="h-28 w-28 cursor-zoom-in select-none rounded-lg border border-zinc-800 object-cover transition-opacity hover:opacity-90"
                  data-testid="ro-tour-sheet-image"
                />
              </div>
            )}
          </div>
          <p className="mt-3 text-xs text-zinc-500">Tap a photo to view full screen · pinch or double-tap to zoom · drag to pan</p>
        </div>
      )}

      <ImageZoomViewer src={viewer.src} alt={viewer.alt} open={viewer.open} onClose={() => setViewer({ open: false, src: null, alt: "" })} testid="ro-photo-viewer" />
    </div>
  );
};
